import { auStateTimezoneUtils } from "./au-state-timezone";

const AU_LOCALE = "en-AU";
const SLOT_COUNT = 24;
const SLOT_DURATION_MINUTES = 30;
const FIRST_SLOT_START_MINUTES = 9 * 60;

export interface InspectionSlotDateGroup<T> {
    dateKey: string;
    dateLabel: string;
    slots: T[];
}

/** Half-hour open-inspection slot from 9:00 AM (index 0) through 8:30–9:00 PM. */
export interface OpenInspectionTimeSlotOption {
    /** Start time as HH:mm (e.g. "09:00"). */
    id: string;
    index: number;
    label: string;
}

export type OpenInspectionLabelStyle = "compact" | "padded";

function toDate(value: Date | string | number): Date {
    return value instanceof Date ? value : new Date(value);
}

function formatMinutesToLabel(totalMin: number, style: OpenInspectionLabelStyle): string {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    if (style === "padded") {
        return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
    }
    return `${h12}${m ? `:${String(m).padStart(2, "0")}` : ""} ${ampm}`;
}

function minutesToTimeId(totalMin: number): string {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Half-hour slots from 9 AM to 9 PM. */
export function getOpenInspectionTimeSlots(
    options?: { labelStyle?: OpenInspectionLabelStyle },
): OpenInspectionTimeSlotOption[] {
    const style = options?.labelStyle ?? "compact";
    const separator = style === "padded" ? " - " : " – ";
    const slots: OpenInspectionTimeSlotOption[] = [];

    for (let i = 0; i < SLOT_COUNT; i++) {
        const startMin = FIRST_SLOT_START_MINUTES + i * SLOT_DURATION_MINUTES;
        const endMin = startMin + SLOT_DURATION_MINUTES;
        slots.push({
            id: minutesToTimeId(startMin),
            index: i,
            label: `${formatMinutesToLabel(startMin, style)}${separator}${formatMinutesToLabel(endMin, style)}`,
        });
    }
    return slots;
}

/** Compact labels (e.g. "9 AM – 9:30 AM") — admin / mobile open-slot pickers. */
export const OPEN_INSPECTION_TIME_SLOTS = getOpenInspectionTimeSlots();

/** Padded labels (e.g. "09:00 AM - 09:30 AM") — web / admin walk-in dropdowns. */
export const OPEN_INSPECTION_TIME_SLOTS_PADDED = getOpenInspectionTimeSlots({
    labelStyle: "padded",
});

export function getSlotDateTimes(
    date: Date,
    slotIndex: number,
    timeZone: string,
): { start: Date; end: Date } {
    const startMinutes = FIRST_SLOT_START_MINUTES + slotIndex * SLOT_DURATION_MINUTES;
    const endMinutes = startMinutes + SLOT_DURATION_MINUTES;
    const { year, month, day } = auStateTimezoneUtils.getWallDateParts(date);

    const start = auStateTimezoneUtils.zonedTimeToUtc(
        {
            year,
            month,
            day,
            hour: Math.floor(startMinutes / 60),
            minute: startMinutes % 60,
        },
        timeZone,
    );
    const end = auStateTimezoneUtils.zonedTimeToUtc(
        {
            year,
            month,
            day,
            hour: Math.floor(endMinutes / 60),
            minute: endMinutes % 60,
        },
        timeZone,
    );
    return { start, end };
}

/** Convert a picker date + HH:mm slot id into start/end instants in the property TZ. */
export function getSlotDateTimesById(
    date: Date,
    slotId: string,
    timeZone: string,
): { start: Date; end: Date } {
    const [h, m] = slotId.split(":").map(Number);
    const { year, month, day } = auStateTimezoneUtils.getWallDateParts(date);
    const start = auStateTimezoneUtils.zonedTimeToUtc(
        { year, month, day, hour: h ?? 0, minute: m ?? 0 },
        timeZone,
    );
    const end = new Date(start.getTime() + SLOT_DURATION_MINUTES * 60 * 1000);
    return { start, end };
}

/** True when the calendar picker's wall date is before "today" in the property timezone. */
export function isPastWallDate(date: Date, timeZone: string): boolean {
    const today = auStateTimezoneUtils.getTodayInTimezone(timeZone);
    const parts = auStateTimezoneUtils.getWallDateParts(date);
    return (
        parts.year < today.year ||
        (parts.year === today.year && parts.month < today.month) ||
        (parts.year === today.year && parts.month === today.month && parts.day < today.day)
    );
}

/** @deprecated Prefer isPastWallDate. */
export const isDateDisabled = isPastWallDate;

/** True when this index-based slot's start instant is already in the past. */
export function isSlotStartInPast(
    date: Date,
    slotIndex: number,
    timeZone: string,
): boolean {
    const { start } = getSlotDateTimes(date, slotIndex, timeZone);
    return start.getTime() <= Date.now();
}

/** True when this HH:mm slot id's start instant is already in the past. */
export function isOpenSlotStartInPast(
    date: Date,
    slotId: string,
    timeZone: string,
): boolean {
    const { start } = getSlotDateTimesById(date, slotId, timeZone);
    return start.getTime() <= Date.now();
}

export function formatInspectionTime(
    d: Date | string | undefined | null,
    timeZone?: string,
): string {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString(AU_LOCALE, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        ...(timeZone ? { timeZone } : {}),
    });
}

export function formatInspectionDisplayDate(
    date: Date | undefined,
    timeZone?: string,
    emptyLabel = "Select a date",
): string {
    if (!date) return emptyLabel;
    return date.toLocaleDateString(AU_LOCALE, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        ...(timeZone ? { timeZone } : {}),
    });
}

/** Picker wall day as YYYY-MM-DD (avoids toISOString day-shift). */
export function toWallDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD into a local Date for calendar display. */
export function parseWallDateKey(value: string): Date | undefined {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
    const [y, m, d] = value.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Build a picker-compatible Date from wall Y-M-D parts. */
export function toPickerDate(parts: { year: number; month: number; day: number }): Date {
    return new Date(parts.year, parts.month - 1, parts.day);
}

/** Calendar Date for "today" in the property timezone (picker-compatible). */
export function getPropertyTodayPickerDate(timeZone: string): Date {
    return toPickerDate(auStateTimezoneUtils.getTodayInTimezone(timeZone));
}

export function groupInspectionSlotsByDate<T>(
    slots: T[],
    getStartTime: (slot: T) => Date | string,
    timeZone: string,
): InspectionSlotDateGroup<T>[] {
    const map = new Map<string, T[]>();

    for (const slot of slots) {
        const key = auStateTimezoneUtils.formatDateKey(getStartTime(slot), timeZone);
        const list = map.get(key) ?? [];
        list.push(slot);
        map.set(key, list);
    }

    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, items]) => ({
            dateKey,
            dateLabel: auStateTimezoneUtils.formatDateLabel(dateKey, timeZone),
            slots: items.sort(
                (a, b) => toDate(getStartTime(a)).getTime() - toDate(getStartTime(b)).getTime(),
            ),
        }));
}

export type ScheduledInspectionType = "open" | "private";

export interface PartitionedInspectionSlots<T> {
    upcoming: T[];
    past: T[];
}

export interface TypedInspectionSlotSections<T> {
    open: T[];
    private: T[];
}

/** Split slots into upcoming (start >= now) and past (start < now), each sorted by start time. */
export function partitionInspectionSlotsByTiming<T>(
    slots: T[],
    getStartTime: (slot: T) => Date | string,
    nowMs: number = Date.now(),
): PartitionedInspectionSlots<T> {
    const upcoming: T[] = [];
    const past: T[] = [];

    for (const slot of slots) {
        const startMs = toDate(getStartTime(slot)).getTime();
        if (Number.isNaN(startMs)) continue;
        if (startMs >= nowMs) upcoming.push(slot);
        else past.push(slot);
    }

    const byStartAsc = (a: T, b: T) =>
        toDate(getStartTime(a)).getTime() - toDate(getStartTime(b)).getTime();
    const byStartDesc = (a: T, b: T) =>
        toDate(getStartTime(b)).getTime() - toDate(getStartTime(a)).getTime();

    upcoming.sort(byStartAsc);
    past.sort(byStartDesc);

    return { upcoming, past };
}

/** Group slots into open (public) vs private lists, preserving input order. */
export function groupInspectionSlotsByType<T>(
    slots: T[],
    getType: (slot: T) => ScheduledInspectionType | string,
): TypedInspectionSlotSections<T> {
    const open: T[] = [];
    const privateSlots: T[] = [];

    for (const slot of slots) {
        if (getType(slot) === "private") privateSlots.push(slot);
        else open.push(slot);
    }

    return { open, private: privateSlots };
}

/** Partition by upcoming/past, then by open/private within each bucket. */
export function partitionScheduledInspections<T>(
    slots: T[],
    getStartTime: (slot: T) => Date | string,
    getType: (slot: T) => ScheduledInspectionType | string,
    nowMs: number = Date.now(),
): {
    upcoming: TypedInspectionSlotSections<T>;
    past: TypedInspectionSlotSections<T>;
} {
    const { upcoming, past } = partitionInspectionSlotsByTiming(slots, getStartTime, nowMs);
    return {
        upcoming: groupInspectionSlotsByType(upcoming, getType),
        past: groupInspectionSlotsByType(past, getType),
    };
}

/** @deprecated Use groupInspectionSlotsByDate with property timezone. */
export function groupInspectionSlotsByLocalDate<T>(
    slots: T[],
    getStartTime: (slot: T) => Date | string,
): InspectionSlotDateGroup<T>[] {
    const timeZone = auStateTimezoneUtils.getTimezoneForState(null);
    return groupInspectionSlotsByDate(slots, getStartTime, timeZone);
}

/** @deprecated Use auStateTimezoneUtils.formatDateKey with property timezone. */
export function toLocalDateKey(date: Date | string | number): string {
    const d = toDate(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/** Up to two initials for avatar preview (matches attendee list avatars). */
export function getAttendeeAvatarInitial(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "?";

  if (trimmed.includes("@")) {
    const local = trimmed.split("@")[0]?.trim();
    if (local) return local.charAt(0).toUpperCase();
  }

  const initials = trimmed
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return initials || "?";
}

/** @deprecated Use auStateTimezoneUtils.formatDateLabel with property timezone. */
export function toLocalDateLabel(dateKey: string): string {
    return auStateTimezoneUtils.formatDateLabel(
        dateKey,
        auStateTimezoneUtils.getTimezoneForState(null),
    );
}
